import axios from 'axios'
import {createStore} from 'vuex'

// eslint-disable-next-line no-undef
axios.defaults.baseURL = process.env.VUE_APP_API_URL

// store the token in localstorage if set
const store = createStore({
    state: {
        token: localStorage.getItem('access_token') || null,
        applications: [],
        verifiedEmail: true,
        modules: [],
    },

    // computed property to check whether login token is set.
    getters: {
        verifiedEmail(state) {
            return state.verifiedEmail
        },
        loggedIn(state) {
            return state.token !== null
        },
        applicationDetails(state) {
            return state.applications.application
        },
        applicationGroups(state) {
            return state.applications.groupedApplications
        },
        applicationsData(state) {
            return state.applications
        },
        modules(state) {
            return state.modules
        },
        settings(state) {
            return state.settings
        },
        programmesList(state) {
            return state.applications.programmes
        },
        coursesList(state) {
            return state.applications.courses
        },
        agentsList(state) {
            return state.applications.agents
        },
        gendersList(state) {
            return state.applications.genders
        },
        ethnicityList(state) {
            return state.applications.ethnicities
        },
        disabilityList(state) {
            return state.applications.disabilities
        },
        religiousBeliefList(state) {
            return state.applications.religiousBelief
        },
        sexualOrientationList(state) {
            return state.applications.sexualOrientation
        },
        genderIdentityList(state) {
            return state.applications.genderIdentity
        },
        documentTypesList(state) {
            return state.applications.documentTypes
        },
        sourcesOfInterestList(state) {
            return state.applications.sourcesOfInterest
        },
        contactDetailTypesList(state) {
            return state.applications['contactDetailTypes']
        },
        countriesOfDomicileList(state) {
            return state.applications['countriesOfDomicile']
        },
        countriesOfBirthList(state) {
            return state.applications['countriesOfBirth']
        },
        programmeTypesList(state) {
            return state.applications['programmeTypes']
        },
        titlesList(state) {
            return state.applications['titles']
        },
        sections(state) {
            return state.applications['sections']
        },
        modeOfAttendanceList(state) {
            return state.applications['modeOfAttendance']
        },
        proposedStartDateList(state) {
            return state.applications['proposedStartDates']
        },
        scholarshipsList(state) {
            return state.applications['scholarships']
        },
        nationalityList(state) {
            return state.applications['nationality']
        },
        institutionsList(state) {
            return state.applications['institutions']
        },
        insOverseasOptions(state) {
            return state.applications['insOverseasOptions']
        },
    },

    // change state by committing a mutation
    mutations: {
        retrieveToken(state, token) {
            state.token = token
        },
        destroyToken(state) {
            state.token = null
            localStorage.removeItem('access_token')
        },
        retrieveApplications(state, applications) {
            state.applications = applications
        },
        getModules(state, modules) {
            state.modules = modules
        },
        getSettings(state, settings) {
            state.settings = settings
        },
        clearApplications(state) {
            state.applications = []
        },
        unVerifiedEmail(state) {
            state.verifiedEmail = false
        },
        verifiedEmail(state) {
            state.verifiedEmail = true
        },
    },

    actions: {

        getModules(context) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/modules')
                    .then(response => {
                        context.commit('getModules',response.data?.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        getSettings(context) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/settings')
                    .then(response => {
                        context.commit('getSettings', response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        checkEmailDomain(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/checkEmailDomain?email=' + data.email)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })

        },

        getBasicApplicationInfo(context, application) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/applications/' + application.applicationRef + '/basicInfo')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })

        },

        submitApplication(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/submitApplication', {
                    from: 'Draft',
                    to: 'Submitted',
                    agent: data.agent
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        getPaymentLink(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/applications/' + data.applicationRef + '/getPaymentLink?type=' + data.paymentType)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        checkPaymentStatus(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/applications/' + data.applicationRef + '/checkApplicationPayment?type=' + data.paymentType)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // register applicant (user)
        register(context, data) {
            return new Promise((resolve, reject) => {
                axios.post('/register', {
                    first_name: data.first_name,
                    email: data.email,
                    password: data.password,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        verifyHash(context, data) {
            return new Promise((resolve, reject) => {
                axios.post('/verifyEmailHash', {
                    hash: data.hash,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // clear application from state after logout
        clearApplications(context) {
            context.commit('clearApplications')
        },

        // delete token on logout. pass the authorization header Bearer
        destroyToken(context) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token

            if (context.getters.loggedIn) {
                return new Promise((resolve, reject) => {
                    axios.post('/logout')
                        .then(response => {
                            context.commit('destroyToken')
                            resolve(response)
                        })
                        .catch(error => {
                            reject(error)
                        })
                })
            }
        },


        // get token after user login. wrap in promise to redirect user if successful
        retrieveToken(context, credentials) {

            return new Promise((resolve, reject) => {
                axios.post(process.env.VUE_APP_API_URL + process.env.VUE_APP_PASSPORT_LOGIN_ENDPOINT, {
                    grant_type: 'password',
                    client_id: process.env.VUE_APP_PASSPORT_CLIENT_ID,
                    client_secret: process.env.VUE_APP_PASSPORT_CLIENT_SECRET,
                    username: credentials.username,
                    password: credentials.password,
                })
                    .then(response => {
                        const token = response.data.access_token
                        // this is where you store the token (local or cookie)
                        localStorage.setItem('access_token', token)
                        context.commit('retrieveToken', token)
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // get all applications for logged in user (user should only have one application in progress)
        retrieveApplications(context) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
            return new Promise((resolve, reject) => {
                axios.get('/applications')
                    .then(response => {
                        context.commit('retrieveApplications', response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // get single application for logged in user
        retrieveSingleApplication(context, application) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
            return new Promise((resolve, reject) => {
                axios.get('/applications/' + application.applicationRef + '/edit')
                    .then(response => {
                        context.commit('retrieveApplications', response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })

        },

        // create new application (add user_id to applications table)
        createApplication(context, data) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
            if (context.getters.loggedIn) {
                return new Promise((resolve, reject) => {
                    axios.post('/applications', data)
                        .then(response => {
                            resolve(response)
                        })
                        .catch(error => {
                            reject(error)
                        })
                })
            }
        },
        // update personal details
        updateSectionData(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationId + '/editSection', data.sectionData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        // update personal details
        updatePersonalDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.reference + '/editPersonalDetails', data.personalData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        // update agent details
        updateAgentDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/agents/' + data.reference + '/editAgentDetails', 
                    {
                        agent_code          : data.agent,
                        agent_office_code   : data.agentOffice,
                        agent_office_text   : data.agentOfficeText,
                        no_agent            : data.isAgentChecked
                    })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        // fetch agent details
        fetchAgentOffices(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/agents/' + data + '/agentOfficeDetails')
                    .then(response => {
                        resolve(response.data)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateNationalityDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.reference + '/editNationalityDetails', data.personalData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        createEducationItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/newEducationItem', data.educationItem)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateEducationItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/' + data.educationItemReference + '/editEducationItem', data.educationItem)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        deleteEducationItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.applicationReference + '/' + data.educationReference + '/deleteEducationItem')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        createEnglishTest(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/newEnglishTest', data.englishTest)
                  .then(response => {
                      resolve(response)
                  })
                  .catch(error => {
                      reject(error)
                  })
            })
        },
        updateEnglishTest(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/' + data.englishTest.reference + '/editEnglishTest', data.englishTest)
                  .then(response => {
                      resolve(response)
                  })
                  .catch(error => {
                      reject(error)
                  })
            })
        },
        deleteEnglishTest(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.applicationReference + '/' + data.englishTestReference + '/deleteEnglishTest')
                  .then(response => {
                      resolve(response)
                  })
                  .catch(error => {
                      reject(error)
                  })
            })
        },
        noEnglishTestTaken(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/noEnglishTestTaken')
                  .then(response => {
                      resolve(response)
                  })
                  .catch(error => {
                      reject(error)
                  })
            })
        },

        createReferee(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/newReferee', data.referee)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateReferee(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/' + data.refereeReference + '/editReferee', data.referee)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        deleteReferee(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.applicationReference + '/' + data.refereeReference + '/deleteReferee')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        emailReferee(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/' + data.refereeReference + '/emailReferee')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        // update contact details
        createContactDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/createContactDetails', data.contactData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        deleteContactDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.applicationReference + '/' + data.contactReference + '/deleteContactDetails')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        updateContactDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/' + data.contactReference + '/editContactDetails', {
                    application_id: data.applicationReference,
                    address_line1: data.contactData.address_line1,
                    address_line2: data.contactData.address_line2,
                    address_line3: data.contactData.address_line3,
                    city_town: data.contactData.city_town,
                    post_zip_code: data.contactData.post_zip_code,
                    county_state: data.contactData.county_state,
                    country_id: data.contactData.country_id,
                    phone_number: data.contactData.phone_number,
                    alt_number: data.contactData.alt_number,
                    fax_number: data.contactData.fax_number,
                    type_id: data.contactData.type_id,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // update programme details (pass applicationRef to add a new programme choice for same application)
        updateProgrammeDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/editProgrammeDetails', {
                    selectedProgrammes: data.selectedProgrammes,
                    proposedStartDate: data.proposedStartDate,
                    applicationRef: data.applicationRef,
                    proposedResearchTopic: data.proposedResearchTopic,
                    proposedSupervisor: data.proposedSupervisor,
                    mrcStaff: data.mrcStaff,
                    hasMscDL: data.hasMscDL,
                    mscDLCourse: data.mscDLCourse,
                    completedMscDL: data.completedMscDL,
                    isAlumni: data.isAlumni,
                    alumniCourseInfo: data.alumniCourseInfo,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateProgrammeInstance(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/addProgrammeInstance', {
                    selectedProgramme: data.selectedProgramme,
                    proposedStartDate: data.proposedStartDate,
                    applicationRef: data.applicationRef,
                    proposedResearchTopic: data.proposedResearchTopic,
                    proposedSupervisor: data.proposedSupervisor,
                    mrcStaff: data.mrcStaff,
                    hasMscDL: data.hasMscDL,
                    mscDLCourse: data.mscDLCourse,
                    studentId: data.studentID,
                    otherCurrentModules: data.modules,
                    isAlumni: data.isAlumni,
                    alumniCourseInfo: data.alumniCourseInfo,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete programme choice
        deleteProgrammeDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/deleteProgrammeDetails', {
                    applicationProgrammeRef: data.applicationProgrammeRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        // delete programme choice
        makeActiveChoice(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/makeActiveChoice', {
                    applicationProgrammeRef: data.applicationProgrammeRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // update employment details
        updateEmploymentDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/editEmploymentDetails', {
                    employmentHistory: data.employmentHistory,
                    applicationRef: data.applicationRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        createEmploymentItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/newEmploymentItem', data.employmentItem)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateEmploymentItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/' + data.employmentItemReference + '/editEmploymentItem', data.employmentItem)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        deleteEmploymentItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.applicationReference + '/' + data.employmentReference + '/deleteEmploymentItem')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        // record if applicant has specified no prior employment
        noPriorEmployment(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/noPriorEmployment')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        //get referee portal data
        getRefereePortalData(reference, token) {
            return new Promise((resolve, reject) => {
                axios.get('/referee_portal/' + token.reference + '/' + token.token)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // update referee details
        updateRefereeDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.post('/submit_referee/' + data.reference + '/' + data.token, data.refereeData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // update referee details
        updateReferenceDetails(context, data) {
            return new Promise((resolve, reject) => {
                const url = '/update_reference/' + data.reference + '/' + data.token + '/' + data.referenceId

                axios.put(url, data.referenceData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        updateAndLock(context, data) {
            return new Promise((resolve, reject) => {
                let url = '/lock_reference/' + data.reference + '/' + data.token

                axios.post(url)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        // update personal statement (in applications table)
        updatePersonalStatement(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/editPersonalStatement', data.personalStatement)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // update work experience and mathematics skills (in applications table)
        updateWorkExperienceAndMathsSkills(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/editWorkExperienceAndMathsSkills', data.form)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // upload supporting documents
        uploadDocument(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/newDocument', data.file, {'content-type': 'multipart/form-data'})
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete supporting document item
        deleteDocument(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete('/applications/' + data.documentRef + '/deleteDocument', {
                    documentRef: data.documentRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete supporting document item
        downloadDocument(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                let targetUrl = '/applications/' + data.applicationRef + '/' + data.documentRef + '/downloadDocument'
                axios.get(targetUrl, {
                    responseType: 'blob',
                    timeout: 10000,
                })
                    .then(response => {
                        const url = window.URL.createObjectURL(new Blob([response.data]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute('download', data.fileName)
                        document.body.appendChild(link)
                        link.click()
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        // upload supporting documents
        uploadReferenceDocument(context, data) {
            return new Promise((resolve, reject) => {
                let targetUrl = '/referee_portal/' + data.refereeRef + '/' + data.token + '/' + data.applicationRef + '/newDocument'
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post(targetUrl, data.file, {'content-type': 'multipart/form-data'})
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete supporting document item
        downloadReferenceDocument(context, data) {
            return new Promise((resolve, reject) => {
                let targetUrl = '/referee_portal/' + data.refereeRef + '/' + data.token + '/' + data.documentRef + '/downloadDocument'
                axios.get(targetUrl, {
                    responseType: 'blob',
                    timeout: 10000,
                })
                    .then(response => {
                        const url = window.URL.createObjectURL(new Blob([response.data]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute('download', data.fileName)
                        document.body.appendChild(link)
                        link.click()
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete supporting document item
        deleteReferenceDocument(context, data) {
            return new Promise((resolve, reject) => {
                let targetUrl = '/referee_portal/' + data.refereeRef + '/' + data.token + '/' + data.documentRef + '/deleteDocument'
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.delete(targetUrl, {
                    documentRef: data.documentRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // add new application sponsor item
        newSponsorItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/newSponsorItem', {
                    contact_details: data.sponsorData.contact_details,
                    amount_of_award: data.sponsorData.amount_of_award,
                    method_of_funding: data.applicationData.method_of_funding,
                    lshtm_scholarship: data.applicationData.lshtm_scholarship,
                    scholarship_name: data.applicationData.scholarship_name,
                    scholarship_name_other: data.applicationData.scholarship_name_other,
                    can_self_fund: data.applicationData.can_self_fund,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // delete sponsor item
        deleteSponsorItem(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationRef + '/deleteSponsorItem', {
                    sponsorItemRef: data.sponsorItemRef,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        createEqualOpportunities(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationReference + '/newEqualOpportunities', data.equalOpportunitiesData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateEqualOpportunities(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/applications/' + data.applicationReference + '/editEqualOpportunities', data.equalOpportunitiesData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        // update funding details (application_sponsors table and applications table)
        updateFundingDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.applicationData.reference + '/editFundingDetails', {
                    method_of_funding: data.applicationData.method_of_funding,
                    lshtm_scholarship: data.applicationData.lshtm_scholarship,
                    lshtm_scholarship_understood: data.applicationData.lshtm_scholarship_understood,
                    scholarship_name: data.applicationData.scholarship_name,
                    scholarship_name_other: data.applicationData.scholarship_name_other,
                    can_self_fund: data.applicationData.can_self_fund,
                })
                    .then(() => {
                        axios.post('/applications/' + data.applicationData.reference + '/editSponsorDetails', {
                            applicationSponsor: data.sponsorData,
                        })
                            .then(response => {
                                resolve(response)
                            })
                            .catch(error => {
                                reject(error)
                            })

                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        // Record the user's decision to accept or decline the admissions offer
        recordDecision(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.post('/applications/' + data.reference + '/recordDecision', {
                    userDecision: data.userDecision,
                    userSignature: data.userSignature,
                    rejectionReason: data.rejectionReason,
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        getUserDetails(context) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.get('/profileData', {})
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateUserProfileData(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/profileData', data.userData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateUserPassword(context, data) {
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/updateCredentials', data.passwordData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        checkResetPasswordDetails(context, data) {
            return new Promise((resolve, reject) => {
                axios.post('/checkResetPasswordDetails', data)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updatePasswordWithTokens(context, data) {
            return new Promise((resolve, reject) => {
                axios.put('/updatePassword/' + data.reference + '/' + data.token, data.forgotPasswordData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },


        forgotPassword(context, data) {
            return new Promise((resolve, reject) => {
                axios.post('/forgotPassword', data.forgotPasswordData)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },

        sendReminderEmailToReferee(context, data){
            return new Promise((resolve, reject) => {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + context.state.token
                axios.put('/refereeReminderEmail/' + data.reference + '/')
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
    },


    // get referee portal doc types (no access token required, but valid token session required).
    //get referee portal data
    getDocumentTypesByPortalToken(reference, token) {
        return new Promise((resolve, reject) => {
            axios.get('/referee_portal_getdoctypes/' + token.reference + '/' + token.token)

                .then(response => {
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
        })
    },
    })

export default store
